import { useEffect, useState, useMemo } from 'react';
import { productsAPI, categoriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Plus, Pencil, Trash2, FolderPlus, Loader2, Settings2, Search, Upload } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminMenu() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', image: '', category_id: '', available: true, options: []
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', sort_order: 0 });

  const loadData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        productsAPI.getAll(),
        categoriesAPI.getAll()
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let imageUrl = productForm.image;
      if (imageFile) {
        const uploadResponse = await productsAPI.uploadImage(imageFile);
        imageUrl = uploadResponse.data.url;
      }
      const data = {
        ...productForm,
        allergens: (productForm.allergensText || '').split(',').map(s => s.trim()).filter(Boolean),
        vegetarian: !!productForm.vegetarian,
        alcohol_free: !!productForm.alcohol_free,
        image: imageUrl,
        price: parseFloat(productForm.price || '0'),
        options: productForm.options.map(o => ({
          name: o.name,
          price: parseFloat(o.price) || 0
        })).filter(o => o.name.trim())
      };
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, data);
        toast.success('Produit mis a jour');
      } else {
        await productsAPI.create(data);
        toast.success('Produit cree');
      }
      setProductDialogOpen(false);
      setEditingProduct(null);
      resetProductForm();
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const resetProductForm = () => {
    if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
    setProductForm({ name: '', description: '', price: '', image: '', category_id: '', available: true, options: [] });
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Choisissez une image JPG, PNG ou WebP');
      event.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      event.target.value = '';
      return;
    }
    if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingCategory) {
        await categoriesAPI.update(editingCategory.id, categoryForm);
        toast.success('Categorie mise a jour');
      } else {
        await categoriesAPI.create(categoryForm);
        toast.success('Categorie creee');
      }
      setCategoryDialogOpen(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', sort_order: 0 });
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await productsAPI.delete(id);
      toast.success('Produit supprime');
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette categorie ?')) return;
    try {
      await categoriesAPI.delete(id);
      toast.success('Categorie supprimee');
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditProduct = (product) => {
    setImageFile(null);
    setImagePreview(product.image || '');
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      allergensText: (product.allergens || []).join(', '),
      vegetarian: !!product.vegetarian,
      alcohol_free: !!product.alcohol_free,
      description: product.description,
      price: product.price.toString(),
      image: product.image,
      category_id: product.category_id,
      available: product.available,
      options: (product.options || []).map(o => ({ name: o.name, price: o.price?.toString() || '0' }))
    });
    setProductDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({ name: category.name, sort_order: category.sort_order });
    setCategoryDialogOpen(true);
  };

  const addOption = () => {
    setProductForm(prev => ({
      ...prev,
      options: [...prev.options, { name: '', price: '0' }]
    }));
  };

  const updateOption = (index, field, value) => {
    setProductForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? { ...opt, [field]: value } : opt)
    }));
  };

  const removeOption = (index) => {
    setProductForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: 'Manrope' }}>Menu</h1>
          <p className="text-gray-500">Gerez vos categories et produits</p>
        </div>
        <div className="flex gap-2">
          {/* Category Dialog */}
          <Dialog open={categoryDialogOpen} onOpenChange={(open) => {
            setCategoryDialogOpen(open);
            if (!open) { setEditingCategory(null); setCategoryForm({ name: '', sort_order: 0 }); }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-category-btn">
                <FolderPlus className="w-4 h-4 mr-2" />Categorie
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingCategory ? 'Modifier' : 'Nouvelle'} categorie</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCategorySubmit} className="space-y-4">
                <div>
                  <Label htmlFor="cat-name">Nom</Label>
                  <Input id="cat-name" value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} required data-testid="category-name-input" />
                </div>
                <div>
                  <Label htmlFor="cat-order">Ordre</Label>
                  <Input id="cat-order" type="number" value={categoryForm.sort_order} onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) })} data-testid="category-order-input" />
                </div>
                <Button type="submit" className="w-full" disabled={saving} data-testid="save-category-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Product Dialog */}
          <Dialog open={productDialogOpen} onOpenChange={(open) => {
            setProductDialogOpen(open);
            if (!open) { setEditingProduct(null); resetProductForm(); }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-btn">
                <Plus className="w-4 h-4 mr-2" />Produit
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Modifier' : 'Nouveau'} produit</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleProductSubmit} className="space-y-4">
                <div className="space-y-3 rounded-xl bg-slate-50 p-3">
                  <Label htmlFor="allergens">Allergènes (séparés par des virgules)</Label>
                  <Input id="allergens" value={productForm.allergensText || ''} placeholder="Gluten, lait, fruits à coque…" onChange={e => setProductForm({ ...productForm, allergensText: e.target.value })} />
                  <label className="flex items-center gap-3"><input type="checkbox" checked={!!productForm.vegetarian} onChange={e => setProductForm({ ...productForm, vegetarian: e.target.checked })} /> Végétarien</label>
                  <label className="flex items-center gap-3"><input type="checkbox" checked={!!productForm.alcohol_free} onChange={e => setProductForm({ ...productForm, alcohol_free: e.target.checked })} /> Sans alcool</label>
                </div>
                <div>
                  <Label htmlFor="prod-name">Nom</Label>
                  <Input id="prod-name" value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} required data-testid="product-name-input" />
                </div>
                <div>
                  <Label htmlFor="prod-desc">Description</Label>
                  <Input id="prod-desc" value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} data-testid="product-description-input" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prod-price">Prix (EUR) — 0 pour gratuit</Label>
                    <Input id="prod-price" type="number" min="0" step="0.01" value={productForm.price} onChange={(e) => setProductForm({ ...productForm, price: e.target.value })} required data-testid="product-price-input" />
                  </div>
                  <div>
                    <Label htmlFor="prod-cat">Categorie</Label>
                    <Select value={productForm.category_id} onValueChange={(value) => setProductForm({ ...productForm, category_id: value })}>
                      <SelectTrigger data-testid="product-category-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prod-image">Image du produit</Label>
                  <label htmlFor="prod-image" className="flex min-h-24 cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-3 transition-colors hover:border-violet-400 hover:bg-violet-50">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Aperçu du produit" className="h-20 w-20 rounded-lg object-cover" />
                    ) : (
                      <div className="flex h-20 w-20 items-center justify-center rounded-lg bg-white text-gray-400">
                        <Upload className="h-6 w-6" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium">Choisir une image sur l'appareil</p>
                      <p className="text-xs text-gray-500">JPG, PNG ou WebP · 5 Mo maximum</p>
                    </div>
                  </label>
                  <Input id="prod-image" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} className="sr-only" data-testid="product-image-input" />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="prod-available">Disponible</Label>
                  <Switch id="prod-available" checked={productForm.available} onCheckedChange={(checked) => setProductForm({ ...productForm, available: checked })} data-testid="product-available-switch" />
                </div>

                {/* Product Options Section */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Settings2 className="w-4 h-4 text-gray-500" />
                      <Label className="text-sm font-semibold">Options personnalisables</Label>
                    </div>
                    <Button type="button" variant="outline" size="sm" onClick={addOption} data-testid="add-option-btn">
                      <Plus className="w-3 h-3 mr-1" />Option
                    </Button>
                  </div>
                  {productForm.options.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-2">Aucune option. Ajoutez des extras comme "glace", "sauce"...</p>
                  )}
                  <div className="space-y-2">
                    {productForm.options.map((opt, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                        <Input
                          placeholder="Nom (ex: Extra glace)"
                          value={opt.name}
                          onChange={(e) => updateOption(index, 'name', e.target.value)}
                          className="flex-1 h-8 text-sm"
                          data-testid={`option-name-${index}`}
                        />
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0"
                            value={opt.price}
                            onChange={(e) => updateOption(index, 'price', e.target.value)}
                            className="w-20 h-8 text-sm"
                            data-testid={`option-price-${index}`}
                          />
                          <span className="text-xs text-gray-500">EUR</span>
                        </div>
                        <button type="button" onClick={() => removeOption(index)} className="p-1 hover:bg-red-100 text-red-500 rounded" data-testid={`remove-option-${index}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={saving} data-testid="save-product-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Categories */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Categories</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2 bg-gray-100 rounded-lg px-3 py-2">
                <span className="font-medium">{cat.name}</span>
                <Badge variant="outline" className="text-xs">{cat.sort_order}</Badge>
                <button onClick={() => openEditCategory(cat)} className="p-1 hover:bg-gray-200 rounded" data-testid={`edit-category-${cat.id}`}>
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={() => handleDeleteCategory(cat.id)} className="p-1 hover:bg-red-100 text-red-500 rounded" data-testid={`delete-category-${cat.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Products */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle style={{ fontFamily: 'Manrope' }}>Produits ({products.length})</CardTitle>
          </div>
          <div className="relative mt-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="product-search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {products
              .filter(p => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description?.toLowerCase().includes(searchQuery.toLowerCase()) || p.category_name?.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((product) => (
              <div key={product.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl" data-testid={`product-row-${product.id}`}>
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold truncate">{product.name}</h3>
                    {!product.available && <Badge variant="secondary" className="text-xs">Indisponible</Badge>}
                  </div>
                  <p className="text-sm text-gray-500 truncate">{product.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline">{product.category_name}</Badge>
                    <span className="font-semibold text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                      {product.price === 0 ? 'Gratuit' : `${product.price.toFixed(2)} EUR`}
                    </span>
                    {product.options?.length > 0 && (
                      <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                        {product.options.length} option{product.options.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="icon" onClick={() => openEditProduct(product)} data-testid={`edit-product-${product.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteProduct(product.id)} data-testid={`delete-product-${product.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
